import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSymbolData } from "../dataSource";
import "./ArticlesPanel.css";

/**
 * News articles and their per-article scoring, for production.
 *
 * The DEV panel produces this by shelling out to score_on_demand.py through
 * start.js; Firebase hosting is static, so this reads the export written by
 * daily_sentiment/export_articles_web.py instead.
 *
 * Each article shows:
 *   score   FinBERT's reading AFTER the instrument's polarity rules, which can
 *           legitimately invert it — "pound weakens" is bearish text but bullish
 *           for UK100. Shown as a diverging gauge rather than a raw number.
 * Ordered newest first and faded by age: a fade only reads as intentional if
 * the list is ordered by age, otherwise faded rows interleave with fresh ones
 * and look like a rendering fault.
 */

function scoreClass(v) {
  if (v > 0.05) return "pos";
  if (v < -0.05) return "neg";
  return "neu";
}

function fmtScore(v) {
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}`;
}

function scoreWord(t, v) {
  if (v > 0.4)  return t("newsPanel.wordBullish");
  if (v > 0.05) return t("newsPanel.wordLeansBull");
  if (v < -0.4) return t("newsPanel.wordBearish");
  if (v < -0.05) return t("newsPanel.wordLeansBear");
  return t("newsPanel.wordNeutral");
}

function ageLabel(t, d) {
  if (d == null) return "";
  const h = d * 24;
  if (h < 1)  return t("newsPanel.justNow");
  if (h < 24) return t("newsPanel.hoursAgo", { n: Math.round(h) });
  const days = Math.round(d);
  return days <= 1 ? t("newsPanel.yesterday") : t("newsPanel.daysAgo", { n: days });
}

/** Headlines from the past day read at full strength; older ones fade toward
 *  the back of the 7-day retain window so the list shows at a glance how much
 *  of the signal is current news versus decaying history. */
function ageOpacity(d) {
  if (d == null) return 1;
  if (d <= 1) return 1;                       // past 24h at full strength
  return Math.max(0.3, 1 - (d - 1) * 0.38);   // then fall away quickly
}

export default function ArticlesPanel({ symbol }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!symbol) return;
    let live = true;
    setData(null);
    setFailed(false);
    setFilter("all");
    fetchSymbolData("articles", symbol)
      .then(d => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [symbol]);

  const articles = useMemo(() => {
    const all = Array.isArray(data?.articles) ? data.articles : [];
    if (filter === "bull") return all.filter(a => a.score > 0.05);
    if (filter === "bear") return all.filter(a => a.score < -0.05);
    return all;
  }, [data, filter]);

  if (failed || !data || !articles.length) return null;

  const { total } = data;
  // The bar describes TODAY's news only. Built from the whole 7-day decay
  // window it would summarise a week of sentiment while sitting directly above
  // a list the reader takes as current.
  const tToday = data.today_total ?? 0;
  const tBull  = data.today_bullish ?? 0;
  const tBear  = data.today_bearish ?? 0;
  const tNeu   = data.today_neutral ?? 0;
  const pct = n => (tToday ? (100 * n) / tToday : 0);

  // Counts come from the list these chips actually filter, so a chip can never
  // promise more rows than clicking it produces.
  const listed = Array.isArray(data.articles) ? data.articles : [];
  const filters = [
    ["all",   t("newsPanel.filterAll",  { n: listed.length })],
    ["bull",  t("newsPanel.filterBull", { n: listed.filter(a => a.score > 0.05).length })],
    ["bear",  t("newsPanel.filterBear", { n: listed.filter(a => a.score < -0.05).length })],
  ];

  return (
    <div className="ap-panel">
      <div className="ap-title">{t("newsPanel.title")}</div>
      <p className="ap-sub">
        {t("newsPanel.sub", { total, symbol: data.symbol, date: data.date, shown: data.shown })}
      </p>

      {tToday > 0 && (
        <>
          <div className="ap-split" role="img"
               aria-label={t("newsPanel.ariaSplit", { bull: tBull, neu: tNeu, bear: tBear })}>
            <div className="ap-split-pos" style={{ width: `${pct(tBull)}%` }} />
            <div className="ap-split-neu" style={{ width: `${pct(tNeu)}%` }} />
            <div className="ap-split-neg" style={{ width: `${pct(tBear)}%` }} />
          </div>
          <div className="ap-split-key">
            <span className="ap-split-scope">{t("newsPanel.past24h")} &middot; {tToday}</span>
            <span><b style={{ color: "var(--pos, #16a34a)" }}>{tBull}</b> {t("newsPanel.bullish")}</span>
            {tNeu > 0 && <span><b>{tNeu}</b> {t("newsPanel.neutral")}</span>}
            <span><b style={{ color: "var(--neg, #dc2626)" }}>{tBear}</b> {t("newsPanel.bearish")}</span>
          </div>
        </>
      )}

      <div className="ap-filters">
        {filters.map(([key, label]) => (
          <button key={key} type="button"
                  className={`ap-chip${filter === key ? " is-on" : ""}`}
                  onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </div>

      <div className="ap-list">
        {articles.map((a, i) => {
          const cls  = scoreClass(a.score);
          const mag  = Math.min(Math.abs(a.score), 1) * 50;   // half-track = 50%
          const fresh = a.age_days != null && a.age_days <= 1;
          return (
            <div className="ap-row" key={`${a.title}-${i}`}
                 style={{ opacity: ageOpacity(a.age_days) }}>
              <div className="ap-gauge" title={`${fmtScore(a.score)} — ${scoreWord(t, a.score)}`}>
                <div className="ap-gauge-track">
                  <div className="ap-gauge-mid" />
                  <div className={`ap-gauge-fill ${cls}`}
                       style={a.score >= 0
                         ? { left: "50%", width: `${mag}%` }
                         : { left: `${50 - mag}%`, width: `${mag}%` }} />
                </div>
                <div className={`ap-gauge-word ${cls}`}>{scoreWord(t, a.score)}</div>
              </div>
              <div>
                <div className="ap-headline">{a.title}</div>
                <div className="ap-meta">
                  {a.source && <span>{a.source}</span>}
                  <span className={fresh ? "ap-fresh" : ""}>{ageLabel(t, a.age_days)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="ap-foot">
        {t("newsPanel.foot")}
      </p>
    </div>
  );
}
